import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { 
  BarChart3, TrendingUp, TrendingDown, Download, RefreshCw, Users, Phone,
  Target, PieChart, Activity, Calendar as CalendarIcon, Award, ArrowUp, ArrowDown,
  Clock, Loader2, Filter, ChevronRight, Trophy, Star, Minus, ExternalLink, X, Search, Check
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar
} from 'recharts';
import { 
  getCallTypeTrends, getCallTypeDistribution, getUserPerformanceComparison,
  getUserTrends, getScoreDistribution, getActivityHeatmap, getAnalyticsDashboardSummary,
  getAnalyticsLeaderboard, exportAnalyticsCSV, getUsers, getCalls
, getAvatarDisplayUrl } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AnalyticsCardPopup, ClickableAnalyticsCard } from '@/components/analytics/AnalyticsCardPopup';
import { format, subDays, subMonths } from 'date-fns';

// Color palette for charts
const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const CALL_TYPE_COLORS = {
  job_outreach: '#3b82f6',
  voicemail: '#94a3b8',
  submission_call: '#10b981',
  pre_screening: '#8b5cf6',
  spotlight_call: '#f59e0b',
  interview_request: '#06b6d4',
  interview_prep: '#ec4899',
  interview_debrief: '#84cc16',
  job_offer: '#22c55e',
  counteroffer: '#f97316',
  salary_negotiation: '#eab308',
  onboarding: '#14b8a6',
  placement_checkin: '#a855f7',
  timesheet: '#64748b',
  referral_check: '#0ea5e9',
  client_introduction: '#d946ef',
  follow_up: '#78716c',
  other: '#a1a1aa'
};

export default function AnalyticsDashboard() {
  const { tenant: authTenant } = useAuth();
  const context = useOutletContext();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [tenant, setTenant] = useState(authTenant);
  
  // User filter state (multi-select)
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userFilterOpen, setUserFilterOpen] = useState(false);
  
  // Custom date range state
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60); // seconds
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [callTypeTrends, setCallTypeTrends] = useState(null);
  const [callTypeDistribution, setCallTypeDistribution] = useState(null);
  const [userComparison, setUserComparison] = useState(null);
  const [userTrends, setUserTrends] = useState(null);
  const [scoreDistribution, setScoreDistribution] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  
  // Popup states for clickable cards
  const [popupOpen, setPopupOpen] = useState(null); // 'calls', 'score', 'duration', 'users', 'distribution', 'trends', 'leaderboard', 'heatmap'
  const [popupData, setPopupData] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupPage, setPopupPage] = useState(1);
  const [popupTotal, setPopupTotal] = useState(0);
  const [popupDuration, setPopupDuration] = useState('30d');
  const POPUP_PAGE_SIZE = 100;
  const { adminUrl } = useParams();

  const isDark = theme === 'dark';
  const textColor = isDark ? '#e2e8f0' : '#1e293b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';
  
  // If no tenant from auth, try to load from admin URL
  useEffect(() => {
    if (!authTenant && adminUrl) {
      // Create a minimal tenant object for API calls
      setTenant({ id: adminUrl, _id: adminUrl });
    } else if (authTenant) {
      setTenant(authTenant);
    }
  }, [authTenant, adminUrl]);

  useEffect(() => {
    if (tenant) {
      loadAllData();
      loadUsers();
    }
  }, [tenant, period, selectedUserIds, dateRange]);
  
  // Build filters object for API calls
  const buildFilters = () => {
    const filters = {};
    if (selectedUserIds.length > 0) {
      filters.userIds = selectedUserIds;
    }
    if (period === 'custom' && dateRange.from && dateRange.to) {
      filters.startDate = dateRange.from.toISOString();
      filters.endDate = dateRange.to.toISOString();
    }
    return filters;
  };
  
  // Filtered users for multi-select dropdown
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery) return users;
    return users.filter(u => 
      u.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
    );
  }, [users, userSearchQuery]);
  
  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  // Select/deselect all users
  const handleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u._id || u.id));
    }
  };
  
  // Clear user filter
  const clearUserFilter = () => {
    setSelectedUserIds([]);
    setUserSearchQuery('');
  };
  
  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setDateRange({ from: null, to: null });
    }
  };
  
  // Handle date range selection
  const handleDateRangeSelect = (range) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      setPeriod('custom');
    }
  };
  
  // Apply quick date presets
  const applyQuickDateRange = (days) => {
    const to = new Date();
    const from = subDays(to, days);
    setDateRange({ from, to });
    setPeriod('custom');
    setDatePickerOpen(false);
  };
  
  // Auto-refresh effect
  useEffect(() => {
    let intervalId;
    if (autoRefresh && tenant) {
      intervalId = setInterval(() => {
        loadAllData(true); // silent refresh
        setLastRefresh(new Date());
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, tenant, period]);

  useEffect(() => {
    if (selectedUser && tenant) {
      loadUserTrends(selectedUser);
    }
  }, [selectedUser, tenant, period]);

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAllData = async (silent = false) => {
    if (!tenant?.id) return;
    if (!silent) setLoading(true);
    
    const filters = buildFilters();
    const effectivePeriod = period === 'custom' ? '30d' : period; // Use 30d as fallback for custom
    
    try {
      const [summaryRes, trendsRes, distRes, compRes, scoreRes, heatRes, leaderRes] = await Promise.all([
        getAnalyticsDashboardSummary(tenant.id, effectivePeriod, filters),
        getCallTypeTrends(tenant.id, effectivePeriod, filters),
        getCallTypeDistribution(tenant.id, effectivePeriod, filters),
        getUserPerformanceComparison(tenant.id, effectivePeriod, 'score', filters),
        getScoreDistribution(tenant.id, effectivePeriod, filters),
        getActivityHeatmap(tenant.id, effectivePeriod, filters),
        getAnalyticsLeaderboard(tenant.id, effectivePeriod, 'score', 10, filters)
      ]);
      
      setSummary(summaryRes.data);
      setCallTypeTrends(trendsRes.data);
      setCallTypeDistribution(distRes.data);
      setUserComparison(compRes.data);
      setScoreDistribution(scoreRes.data);
      setHeatmap(heatRes.data);
      setLeaderboard(leaderRes.data);
      
      if (!silent) setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadUserTrends = async (userId) => {
    if (!tenant?._id || !userId) return;
    try {
      const res = await getUserTrends(userId, tenant._id, period);
      setUserTrends(res.data);
    } catch (error) {
      console.error('Error loading user trends:', error);
    }
  };

  const handleExport = async (reportType) => {
    if (!tenant?.id) return;
    setExporting(true);
    try {
      const res = await exportAnalyticsCSV(tenant.id, reportType, period);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `callsense_${reportType}_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };
  
  // Popup handlers for clickable cards
  const openPopup = async (type, initialData = null) => {
    setPopupOpen(type);
    setPopupPage(1);
    setPopupDuration(period);
    
    if (initialData) {
      setPopupData(initialData);
      setPopupTotal(initialData.length);
      setPopupLoading(false);
    } else {
      await loadPopupData(type, 1, period);
    }
  };
  
  const loadPopupData = async (type, page, dur) => {
    setPopupLoading(true);
    try {
      switch (type) {
        case 'calls':
          const callsRes = await getCalls({ page, limit: POPUP_PAGE_SIZE, period: dur });
          setPopupData(callsRes.data.calls || []);
          setPopupTotal(callsRes.data.total || 0);
          break;
        case 'users':
          const usersRes = await getUsers();
          // Show ALL users (not just those with calls_analyzed > 0)
          const allUsers = usersRes.data || [];
          setPopupData(allUsers);
          setPopupTotal(allUsers.length);
          break;
        case 'distribution':
          // Call type distribution - use the already loaded data
          if (callTypeDistribution?.distribution) {
            setPopupData(callTypeDistribution.distribution);
            setPopupTotal(callTypeDistribution.distribution.length);
          }
          break;
        case 'score':
          // Score distribution - use the already loaded data
          if (scoreDistribution?.distribution) {
            setPopupData(scoreDistribution.distribution);
            setPopupTotal(scoreDistribution.distribution.length);
          }
          break;
        case 'trends':
          // Call trends - use the already loaded data
          if (callTypeTrends?.daily_trends) {
            setPopupData(callTypeTrends.daily_trends);
            setPopupTotal(callTypeTrends.daily_trends.length);
          }
          break;
        case 'leaderboard':
          const leaderRes = await getAnalyticsLeaderboard(tenant.id, dur, 'score', 50);
          setPopupData(leaderRes.data?.leaderboard || []);
          setPopupTotal(leaderRes.data?.leaderboard?.length || 0);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading popup data:', error);
      toast.error('Failed to load detailed data');
    } finally {
      setPopupLoading(false);
    }
  };
  
  const handlePopupPageChange = (newPage) => {
    setPopupPage(newPage);
    if (popupOpen === 'calls') {
      loadPopupData('calls', newPage, popupDuration);
    }
  };
  
  const handlePopupDurationChange = (newDuration) => {
    setPopupDuration(newDuration);
    setPopupPage(1);
    loadPopupData(popupOpen, 1, newDuration);
  };
  
  const closePopup = () => {
    setPopupOpen(null);
    setPopupData([]);
    setPopupPage(1);
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <ArrowUp className="h-3 w-3 text-emerald-500" />;
    if (change < 0) return <ArrowDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };
  
  // Column definitions for popup tables
  const popupColumns = {
    calls: [
      { key: 'user_name', label: 'User Name', render: (v, row) => row.user?.name || row.user_name || row.created_by || row.assigned_to || '-' },
      { key: 'filename', label: 'Call', render: (v, row) => (
        <a 
          href={`/dashboard/call/${row.id || row._id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-sm flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          data-testid={`call-link-${row.id || row._id}`}
        >
          {v?.substring(0, 30)}{v?.length > 30 ? '...' : ''}
          <ExternalLink className="h-3 w-3" />
        </a>
      )},
      { key: 'call_type', label: 'Type', render: (v) => <Badge variant="outline" className="capitalize">{v?.replace(/_/g, ' ')}</Badge> },
      { key: 'average_score', label: 'Score', render: (v) => <Badge className={v >= 7 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : v >= 5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}>{v?.toFixed(1) || '-'}</Badge> },
      { key: 'duration', label: 'Duration', render: (v) => `${Math.floor((v || 0) / 60)}:${String((v || 0) % 60).padStart(2, '0')}` },
      { key: 'call_date', label: 'Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' }
    ],
    users: [
      { key: 'name', label: 'User', render: (v, row) => (
        <a 
          href={`/dashboard/user-session?userId=${row.id || row._id}`}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          onClick={(e) => e.stopPropagation()}
          data-testid={`user-popup-link-${row.id || row._id}`}
        >
          {v || '-'}
        </a>
      )},
      { key: 'email', label: 'Email' },
      { key: 'calls_analyzed', label: 'Calls Analyzed', render: (v) => v || 0 },
      { key: 'average_score', label: 'Avg Score', render: (v) => v?.toFixed(1) || '-' }
    ],
    distribution: [
      { key: 'call_type', label: 'Call Type', render: (v) => <span className="capitalize">{v?.replace('_', ' ')}</span> },
      { key: 'count', label: 'Count' },
      { key: 'percentage', label: 'Percentage', render: (v) => `${v?.toFixed(1)}%` },
      { key: 'avg_score', label: 'Avg Score', render: (v) => v?.toFixed(1) || '-' }
    ],
    score: [
      { key: 'range', label: 'Score Range' },
      { key: 'count', label: 'Number of Calls' },
      { key: 'percentage', label: 'Percentage', render: (v) => `${v?.toFixed(1)}%` }
    ],
    trends: [
      { key: 'date', label: 'Date' },
      { key: 'total_calls', label: 'Total Calls' },
      { key: 'avg_score', label: 'Avg Score', render: (v) => v?.toFixed(1) || '-' },
      { key: 'total_duration', label: 'Duration (min)', render: (v) => Math.round((v || 0) / 60) }
    ],
    leaderboard: [
      { key: 'rank', label: 'Rank', render: (v, row) => <Badge variant={v <= 3 ? 'default' : 'outline'}>{v}</Badge> },
      { key: 'name', label: 'User' },
      { key: 'total_calls', label: 'Calls' },
      { key: 'avg_score', label: 'Avg Score', render: (v) => <span className="font-semibold">{v?.toFixed(1)}</span> },
      { key: 'total_duration', label: 'Duration', render: (v) => `${Math.round((v || 0) / 60)} min` }
    ]
  };
  
  // Popup title and descriptions
  const popupInfo = {
    calls: { title: 'All Calls', description: 'Detailed list of all analyzed calls' },
    users: { title: 'Users', description: 'All team members in your organization' },
    distribution: { title: 'Call Type Distribution', description: 'Breakdown of calls by type' },
    score: { title: 'Score Distribution', description: 'Distribution of call quality scores' },
    trends: { title: 'Call Trends Over Time', description: 'Daily call volume and score trends' },
    leaderboard: { title: 'Performance Leaderboard', description: 'Top performers by average score' }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Comprehensive insights into call performance and trends</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => loadAllData()} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Select onValueChange={handleExport} disabled={exporting}>
              <SelectTrigger className="w-[140px]" data-testid="export-select">
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                <span>Export</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user_performance">User Performance</SelectItem>
                <SelectItem value="call_types">Call Types</SelectItem>
                <SelectItem value="trends">Daily Trends</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span className="font-medium">Filters:</span>
          </div>
          
          {/* User Multi-Select Filter */}
          <Popover open={userFilterOpen} onOpenChange={setUserFilterOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className={cn(
                  "h-9 justify-start min-w-[180px]",
                  selectedUserIds.length > 0 && "border-primary bg-primary/5"
                )}
                data-testid="user-filter-trigger"
              >
                <Users className="h-4 w-4 mr-2" />
                {selectedUserIds.length === 0 ? (
                  "All Users"
                ) : selectedUserIds.length === 1 ? (
                  users.find(u => (u._id || u.id) === selectedUserIds[0])?.name || "1 User"
                ) : (
                  `${selectedUserIds.length} Users`
                )}
                {selectedUserIds.length > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {selectedUserIds.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <div className="p-3 border-b">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="h-8 border-0 focus-visible:ring-0 p-0"
                    data-testid="user-search-input"
                  />
                </div>
              </div>
              <div className="p-2 border-b">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start text-xs"
                  onClick={handleSelectAllUsers}
                >
                  {selectedUserIds.length === users.length ? (
                    <>
                      <X className="h-3 w-3 mr-2" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Check className="h-3 w-3 mr-2" />
                      Select All
                    </>
                  )}
                </Button>
              </div>
              <div className="max-h-[250px] overflow-auto p-2">
                {filteredUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
                ) : (
                  filteredUsers.map((user, index) => {
                    const userId = user._id || user.id;
                    const isSelected = selectedUserIds.includes(userId);
                    return (
                      <div
                        key={`user-${userId}-${index}`}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-muted",
                          isSelected && "bg-primary/10"
                        )}
                        onClick={() => toggleUserSelection(userId)}
                        data-testid={`user-option-${userId}`}
                      >
                        <Checkbox checked={isSelected} className="pointer-events-none" />
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback className="text-xs">{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {selectedUserIds.length > 0 && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-xs text-destructive hover:text-destructive"
                    onClick={clearUserFilter}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear Selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Period/Date Range Filter */}
          <Select value={period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[150px] h-9" data-testid="period-select">
              <CalendarIcon className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="12m">Last 12 months</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Custom Date Range Picker */}
          {period === 'custom' && (
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={cn(
                    "h-9 justify-start min-w-[220px]",
                    dateRange.from && dateRange.to && "border-primary bg-primary/5"
                  )}
                  data-testid="date-range-trigger"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {dateRange.from && dateRange.to ? (
                    `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
                  ) : (
                    "Select date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium">Quick Presets</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyQuickDateRange(14)}>
                      14 days
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyQuickDateRange(60)}>
                      60 days
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyQuickDateRange(180)}>
                      6 months
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyQuickDateRange(365)}>
                      1 year
                    </Button>
                  </div>
                </div>
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from || new Date()}
                  selected={dateRange}
                  onSelect={handleDateRangeSelect}
                  numberOfMonths={2}
                  disabled={(date) => date > new Date() || date < subMonths(new Date(), 12)}
                  data-testid="date-range-calendar"
                />
                <div className="p-3 border-t flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setDateRange({ from: null, to: null });
                      setPeriod('30d');
                      setDatePickerOpen(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setDatePickerOpen(false)}
                    disabled={!dateRange.from || !dateRange.to}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          {/* Active Filter Pills */}
          {(selectedUserIds.length > 0 || (period === 'custom' && dateRange.from && dateRange.to)) && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-muted-foreground">Active:</span>
              {selectedUserIds.length > 0 && (
                <Badge variant="secondary" className="gap-1">
                  {selectedUserIds.length} user{selectedUserIds.length > 1 ? 's' : ''}
                  <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={clearUserFilter} />
                </Badge>
              )}
              {period === 'custom' && dateRange.from && dateRange.to && (
                <Badge variant="secondary" className="gap-1">
                  {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d')}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => {
                      setDateRange({ from: null, to: null });
                      setPeriod('30d');
                    }} 
                  />
                </Badge>
              )}
            </div>
          )}
          
          {/* Auto-Refresh Controls */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-background ml-auto">
            <Button
              variant={autoRefresh ? "default" : "ghost"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={cn("h-7 px-2", autoRefresh && "bg-emerald-600 hover:bg-emerald-700")}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1", autoRefresh && "animate-spin")} />
              {autoRefresh ? "On" : "Auto"}
            </Button>
            {autoRefresh && (
              <Select value={String(refreshInterval)} onValueChange={(v) => setRefreshInterval(Number(v))}>
                <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="refresh-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30s</SelectItem>
                  <SelectItem value="60">1m</SelectItem>
                  <SelectItem value="120">2m</SelectItem>
                  <SelectItem value="300">5m</SelectItem>
                </SelectContent>
              </Select>
            )}
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Summary Metrics - Clickable Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ClickableAnalyticsCard onClick={() => openPopup('calls')}>
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-3xl font-bold text-foreground">{summary.metrics.total_calls.toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {getChangeIcon(summary.metrics.calls_change_pct)}
                      <span className={cn("text-sm", summary.metrics.calls_change_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                        {Math.abs(summary.metrics.calls_change_pct)}%
                      </span>
                      <span className="text-xs text-muted-foreground">vs prev period</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Phone className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-blue-600 dark:text-blue-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view details</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('score')}>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                    <p className="text-3xl font-bold text-foreground">{summary.metrics.avg_score}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {getChangeIcon(summary.metrics.score_change)}
                      <span className={cn("text-sm", summary.metrics.score_change >= 0 ? "text-emerald-500" : "text-red-500")}>
                        {summary.metrics.score_change > 0 ? '+' : ''}{summary.metrics.score_change}
                      </span>
                      <span className="text-xs text-muted-foreground">points</span>
                    </div>
                  </div>
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <Target className="h-6 w-6 text-emerald-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-emerald-600 dark:text-emerald-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view distribution</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('trends')}>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Duration</p>
                    <p className="text-3xl font-bold text-foreground">{summary.metrics.total_duration_hours}h</p>
                    <p className="text-xs text-muted-foreground mt-1">Call time analyzed</p>
                  </div>
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <Clock className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-purple-600 dark:text-purple-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view trends</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('users')}>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Users</p>
                    <p className="text-3xl font-bold text-foreground">{summary.metrics.active_users}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total team members</p>
                  </div>
                  <div className="p-3 bg-amber-500/20 rounded-xl">
                    <Users className="h-6 w-6 text-amber-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-amber-600 dark:text-amber-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view users</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="call-types" data-testid="tab-call-types">
            <PieChart className="h-4 w-4 mr-2" />
            Call Types
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="h-4 w-4 mr-2" />
            User Analytics
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Call Type Trends */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    Call Type Trends
                  </CardTitle>
                  <CardDescription>Distribution over time</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => openPopup('trends')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Expand
                </Button>
              </CardHeader>
              <CardContent>
                {callTypeTrends && callTypeTrends.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={callTypeTrends.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} />
                      <YAxis tick={{ fill: textColor, fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1e293b' : '#fff',
                          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      {callTypeTrends.call_types.slice(0, 5).map((type, idx) => (
                        <Area 
                          key={type}
                          type="monotone" 
                          dataKey={type} 
                          stackId="1"
                          stroke={CALL_TYPE_COLORS[type] || CHART_COLORS[idx]}
                          fill={CALL_TYPE_COLORS[type] || CHART_COLORS[idx]}
                          fillOpacity={0.6}
                          name={type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Score Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-emerald-500" />
                    Score Distribution
                  </CardTitle>
                  <CardDescription>Calls by score range</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => openPopup('score')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Expand
                </Button>
              </CardHeader>
              <CardContent>
                {scoreDistribution && scoreDistribution.distribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={scoreDistribution.distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis dataKey="range" tick={{ fill: textColor, fontSize: 10 }} angle={-15} />
                      <YAxis tick={{ fill: textColor, fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1e293b' : '#fff',
                          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                        {scoreDistribution.distribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={
                              entry.min_score >= 8 ? '#10b981' :
                              entry.min_score >= 6 ? '#3b82f6' :
                              entry.min_score >= 4 ? '#f59e0b' :
                              entry.min_score >= 2 ? '#f97316' : '#ef4444'
                            } 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No score distribution data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Call Types */}
          {summary && summary.top_call_types.length > 0 && (
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle>Top Performing Call Types</CardTitle>
                  <CardDescription>By average score</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => openPopup('distribution')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Expand
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {summary.top_call_types.map((ct, idx) => (
                    <div 
                      key={ct.call_type}
                      className={cn(
                        "p-4 rounded-lg border",
                        isDark ? "bg-slate-800/50 border-slate-700" : "bg-muted/50 border-slate-200"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: CALL_TYPE_COLORS[ct.call_type] || CHART_COLORS[idx] }}
                        />
                        <span className="text-sm font-medium truncate">
                          {ct.call_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-2xl font-bold">{ct.avg_score}</p>
                      <p className="text-xs text-muted-foreground">{ct.count} calls</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Call Types Tab */}
        <TabsContent value="call-types" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Call Type Distribution</CardTitle>
                <CardDescription>Breakdown by call type</CardDescription>
              </CardHeader>
              <CardContent>
                {callTypeDistribution && callTypeDistribution.distribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <RechartsPie>
                      <Pie
                        data={callTypeDistribution.distribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => percentage > 5 ? `${percentage}%` : ''}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="name"
                      >
                        {callTypeDistribution.distribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={CALL_TYPE_COLORS[entry.call_type] || CHART_COLORS[index % CHART_COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1e293b' : '#fff',
                          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => [
                          `${value} calls (${props.payload.percentage}%)`,
                          props.payload.name
                        ]}
                      />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                    No distribution data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Call Types Table */}
            <Card>
              <CardHeader>
                <CardTitle>Call Type Details</CardTitle>
                <CardDescription>Performance by call type</CardDescription>
              </CardHeader>
              <CardContent>
                {callTypeDistribution && (
                  <div className="max-h-[350px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Call Type</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">%</TableHead>
                          <TableHead className="text-right">Avg Score</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {callTypeDistribution.distribution.map((ct, idx) => (
                          <TableRow key={ct.call_type}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: CALL_TYPE_COLORS[ct.call_type] || CHART_COLORS[idx] }}
                                />
                                <span className="font-medium">{ct.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{ct.count}</TableCell>
                            <TableCell className="text-right">{ct.percentage}%</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={ct.avg_score >= 7 ? "default" : ct.avg_score >= 5 ? "secondary" : "destructive"}>
                                {ct.avg_score}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          {/* User Selector */}
          <Card>
            <CardHeader>
              <CardTitle>Individual User Analysis</CardTitle>
              <CardDescription>Select a user to view their performance trends</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedUser || ''} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-full md:w-[300px]" data-testid="user-select">
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user._id} value={user._id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {user.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* User Trends */}
          {userTrends && (
            <div className="space-y-6">
              {/* User Summary */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={userTrends.user.avatar_url} />
                      <AvatarFallback className="text-lg">{userTrends.user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold">{userTrends.user.name}</h3>
                      <p className="text-muted-foreground">{userTrends.user.designation}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-6 text-center">
                      <div>
                        <p className="text-2xl font-bold">{userTrends.summary.total_calls}</p>
                        <p className="text-xs text-muted-foreground">Total Calls</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{userTrends.summary.avg_score}</p>
                        <p className="text-xs text-muted-foreground">Avg Score</p>
                      </div>
                      <div>
                        <Badge 
                          variant={userTrends.summary.trend === 'improving' ? 'default' : 
                                   userTrends.summary.trend === 'declining' ? 'destructive' : 'secondary'}
                          className="text-sm"
                        >
                          {userTrends.summary.trend === 'improving' && <TrendingUp className="h-3 w-3 mr-1" />}
                          {userTrends.summary.trend === 'declining' && <TrendingDown className="h-3 w-3 mr-1" />}
                          {userTrends.summary.trend.charAt(0).toUpperCase() + userTrends.summary.trend.slice(1)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User vs Team Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance vs Team Average</CardTitle>
                </CardHeader>
                <CardContent>
                  {userTrends.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={userTrends.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} />
                        <YAxis domain={[0, 10]} tick={{ fill: textColor, fontSize: 11 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="avg_score" 
                          stroke="#3b82f6" 
                          strokeWidth={3}
                          name={userTrends.user.name}
                          dot={{ fill: '#3b82f6' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="team_avg_score" 
                          stroke="#94a3b8" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Team Average"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No trend data for selected period
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* User Comparison Table */}
          {userComparison && (
            <Card>
              <CardHeader>
                <CardTitle>All Users Comparison</CardTitle>
                <CardDescription>Performance metrics for all team members</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Calls</TableHead>
                      <TableHead className="text-right">Avg Score</TableHead>
                      <TableHead className="text-right">vs Team</TableHead>
                      <TableHead className="text-right">Best Calls</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userComparison.users.map((user) => (
                      <TableRow 
                        key={user.user_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedUser(user.user_id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user.avatar_url} />
                              <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{user.designation}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{user.total_calls}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={user.avg_score >= 7 ? "default" : user.avg_score >= 5 ? "secondary" : "destructive"}>
                            {user.avg_score}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn(
                            "flex items-center justify-end gap-1",
                            user.vs_team_avg >= 0 ? "text-emerald-500" : "text-red-500"
                          )}>
                            {user.vs_team_avg >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                            {Math.abs(user.vs_team_avg)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{user.best_calls}</TableCell>
                        <TableCell className="text-right">{user.total_duration_hours}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-6">
          {leaderboard && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top 3 Podium */}
              <Card className="lg:col-span-3 hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-start justify-between space-y-0">
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    Top Performers
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => openPopup('leaderboard')}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Full Leaderboard
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-center gap-4 py-6">
                    {/* 2nd Place */}
                    {leaderboard.leaderboard[1] && (
                      <div className="flex flex-col items-center">
                        <Avatar className="h-16 w-16 border-4 border-slate-400 dark:border-slate-500">
                          <AvatarImage src={leaderboard.leaderboard[1].avatar_url} />
                          <AvatarFallback>{leaderboard.leaderboard[1].name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="mt-2 text-center">
                          <p className="font-medium">{leaderboard.leaderboard[1].name}</p>
                          <p className="text-2xl font-bold text-muted-foreground dark:text-muted-foreground/70">{leaderboard.leaderboard[1].avg_score}</p>
                        </div>
                        <div className="w-20 h-24 bg-slate-200 dark:bg-slate-700 rounded-t-lg flex items-center justify-center mt-2">
                          <span className="text-2xl font-bold text-muted-foreground dark:text-muted-foreground">2</span>
                        </div>
                      </div>
                    )}
                    
                    {/* 1st Place */}
                    {leaderboard.leaderboard[0] && (
                      <div className="flex flex-col items-center">
                        <div className="relative">
                          <Avatar className="h-20 w-20 border-4 border-amber-500">
                            <AvatarImage src={leaderboard.leaderboard[0].avatar_url} />
                            <AvatarFallback>{leaderboard.leaderboard[0].name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="absolute -top-2 -right-2">
                            <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
                          </div>
                        </div>
                        <div className="mt-2 text-center">
                          <p className="font-medium">{leaderboard.leaderboard[0].name}</p>
                          <p className="text-3xl font-bold text-amber-500">{leaderboard.leaderboard[0].avg_score}</p>
                        </div>
                        <div className="w-24 h-32 bg-amber-100 dark:bg-amber-900/30 rounded-t-lg flex items-center justify-center mt-2">
                          <span className="text-3xl font-bold text-amber-500">1</span>
                        </div>
                      </div>
                    )}
                    
                    {/* 3rd Place */}
                    {leaderboard.leaderboard[2] && (
                      <div className="flex flex-col items-center">
                        <Avatar className="h-14 w-14 border-4 border-amber-700">
                          <AvatarImage src={leaderboard.leaderboard[2].avatar_url} />
                          <AvatarFallback>{leaderboard.leaderboard[2].name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="mt-2 text-center">
                          <p className="font-medium">{leaderboard.leaderboard[2].name}</p>
                          <p className="text-xl font-bold text-amber-700">{leaderboard.leaderboard[2].avg_score}</p>
                        </div>
                        <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-t-lg flex items-center justify-center mt-2">
                          <span className="text-2xl font-bold text-amber-700">3</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Full Leaderboard */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle>Full Rankings</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">Rank</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Best Calls</TableHead>
                        <TableHead className="text-right">Improvement</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaderboard.leaderboard.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell>
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center font-bold",
                              user.rank === 1 ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30" :
                              user.rank === 2 ? "bg-muted text-muted-foreground dark:bg-slate-700" :
                              user.rank === 3 ? "bg-amber-50 text-amber-700 dark:bg-amber-900/20" :
                              "bg-muted text-muted-foreground"
                            )}>
                              {user.rank}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={user.avatar_url} />
                                <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-xs text-muted-foreground">{user.designation}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-lg font-bold">{user.avg_score}</span>
                          </TableCell>
                          <TableCell className="text-right">{user.total_calls}</TableCell>
                          <TableCell className="text-right">{user.best_calls}</TableCell>
                          <TableCell className="text-right">
                            <span className={cn(
                              "flex items-center justify-end gap-1",
                              user.improvement >= 0 ? "text-emerald-500" : "text-red-500"
                            )}>
                              {user.improvement >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {user.improvement > 0 ? '+' : ''}{user.improvement}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Analytics Card Popup Modals */}
      {popupOpen && popupInfo[popupOpen] && (
        <AnalyticsCardPopup
          isOpen={!!popupOpen}
          onClose={closePopup}
          title={popupInfo[popupOpen].title}
          description={popupInfo[popupOpen].description}
          data={popupData}
          columns={popupColumns[popupOpen] || []}
          loading={popupLoading}
          totalItems={popupTotal}
          itemsPerPage={POPUP_PAGE_SIZE}
          currentPage={popupPage}
          onPageChange={handlePopupPageChange}
          duration={popupDuration}
          onDurationChange={handlePopupDurationChange}
          exportFilename={`analytics_${popupOpen}`}
        />
      )}
    </div>
  );
}
